import {
  Building2,
  Briefcase,
  Users2,
  GitBranch,
  MapPin,
  Receipt,
  Landmark,
  User,
  Inbox,
  AlertCircle,
  Lock,
} from "lucide-react";
import type { ComponentType } from "react";

import type { ActionDescriptor, BuiltRequest, EntityType, SearchHit } from "../api/searchApi";

export const TYPE_ORDER: EntityType[] = [
  "CLIENT",
  "EMPLOYER",
  "GROUP",
  "DIVISION",
  "LOCATION",
  "BILLING_ACCOUNT",
  "AGENCY",
  "AGENT",
];

export const TYPE_LABEL: Record<EntityType, string> = {
  CLIENT: "Clients",
  EMPLOYER: "Employer Groups",
  GROUP: "Groups",
  DIVISION: "Divisions",
  LOCATION: "Locations",
  BILLING_ACCOUNT: "Billing Accounts",
  AGENCY: "Agencies",
  AGENT: "Agents",
};

export const TYPE_ICON: Record<EntityType, ComponentType<{ className?: string }>> = {
  CLIENT: Building2,
  EMPLOYER: Briefcase,
  GROUP: Users2,
  DIVISION: GitBranch,
  LOCATION: MapPin,
  BILLING_ACCOUNT: Receipt,
  AGENCY: Landmark,
  AGENT: User,
};

export const formatMonth = (ym: string, fmt?: string): string =>
  fmt === "MM/dd/yyyy"
    ? `${ym.slice(5, 7)}/01/${ym.slice(0, 4)}`
    : `${ym.slice(0, 4)}${ym.slice(5, 7)}`;

/** camelCase / snake_case column key → "Title Case" header label. */
export const humanize = (k: string): string =>
  k
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();

/** Long identifiers get monospace + tighter type so they don't dominate the row. */
export const isIdColumn = (k: string): boolean => /(^|[a-z])id$|invoice/i.test(k);

export const formatCell = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

export interface PanelState {
  icon: ComponentType<{ className?: string }>;
  tone: "muted" | "danger";
  title: string;
  detail: string;
}

/** Turn a raw HTTP status / network failure into a friendly, human message. */
export const describeError = (status: number | null, label: string): PanelState => {
  switch (status) {
    case 404:
      return {
        icon: Inbox,
        tone: "muted",
        title: "No records found",
        detail: `There's no ${label.toLowerCase()} data available for this yet.`,
      };
    case 401:
    case 403:
      return {
        icon: Lock,
        tone: "danger",
        title: "Access denied",
        detail: `You don't have permission to view this ${label.toLowerCase()}.`,
      };
    case 0:
      return {
        icon: AlertCircle,
        tone: "danger",
        title: "Can't reach the server",
        detail: "Check your connection and try again.",
      };
    default:
      return {
        icon: AlertCircle,
        tone: "danger",
        title: "Something went wrong",
        detail: status
          ? `The request failed (error ${status}). Please try again.`
          : "The request failed. Please try again.",
      };
  }
};

/**
 * Route the "Open" action to the entity's list page with its ancestor hierarchy
 * pre-selected as filters (e.g. a GROUP opens /groups with its client & employer
 * already selected). Returns null for types without a hierarchy page (AGENCY/AGENT),
 * which fall back to the server-provided detailRoute.
 */
const OPEN_ROUTE: Partial<Record<EntityType, { path: string; params: (keyof SearchHit)[] }>> = {
  CLIENT: { path: "/partners", params: ["clientId"] },
  EMPLOYER: { path: "/employers", params: ["clientId"] },
  GROUP: { path: "/groups", params: ["clientId", "employerId"] },
  DIVISION: { path: "/divisions", params: ["clientId", "employerId", "groupId"] },
  LOCATION: { path: "/locations", params: ["clientId", "employerId", "groupId", "divisionId"] },
};

export function openRoute(hit: SearchHit): string | null {
  const map = OPEN_ROUTE[hit.type];
  if (!map) return null;
  const sp = new URLSearchParams();
  for (const p of map.params) {
    const v = hit[p];
    if (v !== undefined && v !== null && v !== "") sp.set(p, String(v));
  }
  // The Insurance Partners page has no client dropdown — it filters clients by
  // name, so hand it the client's name to narrow the list to the searched client.
  if (hit.type === "CLIENT" && hit.name) sp.set("name", hit.name);
  const qs = sp.toString();
  return `${map.path}${qs ? `?${qs}` : ""}`;
}

/**
 * Where clicking a result row navigates. Never depends on the action catalog:
 * hierarchy route with pre-selected ancestors first, then the server's
 * detailRoute, then a client-side fallback for AGENCY/AGENT detail pages.
 * Returns null only when a hit has no usable target at all.
 */
export function openTarget(hit: SearchHit): string | null {
  const built = openRoute(hit);
  if (built) return built;
  if (hit.detailRoute) return hit.detailRoute;
  if (hit.type === "BILLING_ACCOUNT" && hit.id) return `/billing-accounts/${hit.id}`;
  if (hit.type === "AGENCY" && hit.id) return `/agency-configuration/${hit.id}`;
  if (hit.type === "AGENT" && (hit.brokerId || hit.id))
    return `/agent-configuration/${hit.brokerId ?? hit.id}`;
  return null;
}

/** Build a NAVIGATE target or an API request purely from the descriptor (doc §7). */
export function buildAction(
  hit: SearchHit,
  d: ActionDescriptor,
  month?: string
): { navigateTo: string } | BuiltRequest {
  if (d.kind === "NAVIGATE") {
    // A descriptor that names its own route field (BILLING_ACCOUNT → billingAccountRoute) goes
    // exactly there; only the default "Open" (detailRoute) gets the hierarchy-page treatment.
    if (d.routeField && d.routeField !== "detailRoute") {
      const target = (hit as unknown as Record<string, string | null | undefined>)[d.routeField];
      return { navigateTo: target ?? "" };
    }
    const built = openRoute(hit);
    const fallback = (hit as unknown as Record<string, string>)[d.routeField ?? "detailRoute"];
    return { navigateTo: built ?? fallback ?? "" };
  }
  const values: Record<string, string> = { ...(d.fixedParams ?? {}) };
  for (const [apiParam, source] of Object.entries(d.params ?? {})) {
    const raw = source === "$month" ? month : (hit as unknown as Record<string, unknown>)[source];
    if (raw !== undefined && raw !== null && raw !== "") values[apiParam] = String(raw);
  }
  if (d.paramTarget === "QUERY") {
    const qs = new URLSearchParams(values).toString();
    return { method: d.method ?? "GET", url: `${d.endpoint}${qs ? `?${qs}` : ""}` };
  }
  const body: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) body[k] = /^\d+$/.test(v) ? Number(v) : v;
  return { method: d.method ?? "POST", url: d.endpoint ?? "", body };
}

/** Best-effort extraction of a result table from common response shapes. */
export function pickRows(res: unknown): Record<string, unknown>[] {
  const r = res as Record<string, unknown> | undefined;
  const data = r?.data as Record<string, unknown> | undefined;
  if (Array.isArray(res)) return res as Record<string, unknown>[];
  if (Array.isArray(data?.invoiceMonthDetailsList))
    return data!.invoiceMonthDetailsList as Record<string, unknown>[];
  if (Array.isArray(data?.rows)) return data!.rows as Record<string, unknown>[];
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (Array.isArray(r?.rows)) return r!.rows as Record<string, unknown>[];
  if (Array.isArray(r?.results)) return r!.results as Record<string, unknown>[];
  if (Array.isArray(r?.content)) return r!.content as Record<string, unknown>[];
  return [];
}
